import { defineStore } from "pinia";
import { ref, reactive } from "vue";
import svc from "@/services/userReport.service";
import { createScopedCache } from "@/stores/_cache";
import { toast } from "@/utils/toast";

const userReportListCache = createScopedCache("users:report-list");
const userReportSummaryCache = createScopedCache("users:report-summary");
const USER_REPORT_TTL = 90 * 1000;

function splitCacheParams(params = {}) {
  const { force = false, ...requestParams } = params || {};
  return { force, requestParams };
}

export const useUserReportStore = defineStore("reportUser", () => {
  const items = ref([]);
  const loading = ref(false);
  const reportLoading = ref(false);

  const summary = ref({
    total_users: 0,
    active_users: 0,
    inactive_users: 0,
    new_users: 0
  });

  const chart = ref({
    labels: ["Active", "Inactive"],
    series: [0, 0]
  });

  const meta = ref({ total: 0, per_page: 25, current_page: 1 });

  const filters = reactive({
    role: "",
    status: "",
    sort: "recent",
    date_from: "",
    date_to: "",
    page: 1,
    per_page: 25
  });

  function normalize(u) {
    return {
      id: u.id,
      name: `${u.first_name}`,
      email: u.email,
      role: u.roles?.[0]?.name || "",
      status: u.status,
      avatar: u.avatar_url || "/default.png",
      created_at: u.created_at?.slice(0, 10)
    };
  }

  async function list(extra = {}) {
    loading.value = true;

    try {
      const params = { ...filters, ...extra };
      const { force, requestParams } = splitCacheParams(params);
      const data = await userReportListCache.get(requestParams, {
        ttlMs: USER_REPORT_TTL,
        force,
        loader: async () => {
          const response = await svc.list(requestParams);
          return response.data;
        },
      });

      const arr = data.data || [];

      items.value = arr.map(normalize);
      meta.value = data.meta || meta.value;
    } catch (e) {
      toast(e.message || "Failed to load users", "error");
    } finally {
      loading.value = false;
    }
  }

  async function fetchReport(extra = {}) {
    reportLoading.value = true;

    try {
      const params = { ...filters, ...extra };
      const { force, requestParams } = splitCacheParams(params);
      const data = await userReportSummaryCache.get(requestParams, {
        ttlMs: USER_REPORT_TTL,
        force,
        loader: async () => {
          const response = await svc.report(requestParams);
          return response.data;
        },
      });

      summary.value = data.summary;
      chart.value = data.chart;
    } catch (e) {
      toast(e.message || "Failed to load report", "error");
    } finally {
      reportLoading.value = false;
    }
  }

  async function downloadReport(format) {
    try {
      const params = { ...filters };
      const res = await svc.exportReport(format, params);

      const mime =
        format === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `user-report-${new Date()
        .toISOString()
        .slice(0, 10)}.${format}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast("Failed to export report", "error");
    }
  }

  return {
    items,
    loading,
    reportLoading,
    summary,
    chart,
    meta,
    filters,
    list,
    fetchReport,
    downloadReport
  };
});
